import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../apiBase';

const CHANNEL = 'FreeMansLive';
const KICK_URL = `https://kick.com/${CHANNEL}`;
const POLL_MS = 60 * 1000;
const EDGE = 8; // a dragged widget stays at least this far inside the window
const STORE_KEY = 'kickWidget';

// Per-visitor memory of "I closed the player during this stream", so it doesn't
// pop back open on every page change. Storage can be blocked; then it just forgets.
function loadPrefs() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY)) || {};
  } catch {
    return {};
  }
}
function savePrefs(prefs) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(prefs));
  } catch {
    /* storage unavailable */
  }
}

const shortCount = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, '')}K` : String(n));

function useLiveStatus() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () =>
      apiFetch('/api/live')
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => alive && d && setStatus(d))
        .catch(() => {});
    load();
    const id = setInterval(load, POLL_MS);
    // Coming back to the tab is when people notice a stale "offline", so check right away.
    const onVisible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  return status;
}

function Uptime({ since }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  const mins = Math.max(0, Math.floor((now - since) / 60000));
  return <>{mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`}</>;
}

function Avatar({ src, size, className = '' }) {
  const [broken, setBroken] = useState(false);
  if (src && !broken) {
    return <img src={src} alt="" onError={() => setBroken(true)} className={className} style={{ width: size, height: size }} />;
  }
  return (
    <span className={`kick-avatar-fallback ${className}`} style={{ width: size, height: size, fontSize: size * 0.45 }}>
      {CHANNEL.charAt(0)}
    </span>
  );
}

function Icon({ name, size = 16 }) {
  return (
    <span className="material-symbols-outlined" style={{ fontSize: size }} aria-hidden="true">
      {name}
    </span>
  );
}

export default function KickWidget() {
  const status = useLiveStatus();
  const live = !!status?.live;
  const [view, setView] = useState(null); // 'bubble' | 'card' | 'bar'; null until the status is known
  const [pos, setPos] = useState(null); // null => anchored bottom-right; {left, top} once dragged
  const boxRef = useRef(null);
  const drag = useRef(null);

  // Open the player when a stream starts (unless it was closed during that same
  // stream); fold back to the bubble when it ends. Viewer-count updates don't touch this.
  useEffect(() => {
    if (!status) return;
    if (live && loadPrefs().closedStream !== status.startedAt) setView('card');
    else setView((v) => (live && v ? v : 'bubble'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status && live, status?.startedAt]);

  const clamp = (left, top) => {
    const el = boxRef.current;
    const w = el?.offsetWidth || 0;
    const h = el?.offsetHeight || 0;
    return {
      left: Math.max(EDGE, Math.min(window.innerWidth - w - EDGE, left)),
      top: Math.max(EDGE, Math.min(window.innerHeight - h - EDGE, top)),
    };
  };

  // Keep a dragged widget on screen when the window shrinks or the card changes size.
  useEffect(() => {
    if (!pos) return;
    const onResize = () => setPos((p) => (p ? clamp(p.left, p.top) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!pos]);

  function onPointerDown(e) {
    if (e.button !== 0 || e.target.closest('button, a')) return;
    const r = boxRef.current.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function onPointerMove(e) {
    if (!drag.current) return;
    setPos(clamp(e.clientX - drag.current.dx, e.clientY - drag.current.dy));
  }
  function onPointerUp() {
    drag.current = null;
  }

  function close() {
    if (live) savePrefs({ ...loadPrefs(), closedStream: status.startedAt });
    setView('bubble');
  }

  if (!status || !view) return null;

  if (view === 'bubble') {
    return (
      <button
        type="button"
        onClick={() => setView('card')}
        className={`kick-bubble ${live ? 'is-live' : ''}`}
        title={live ? `${status.channel} is live — watch here` : `${status.channel} is offline`}
        aria-label={live ? `${status.channel} is live, open the player` : `${status.channel} is offline, show channel`}
      >
        <Avatar src={status.avatar} size={50} className="kick-bubble-img" />
        {live ? <span className="kick-bubble-tag">Live</span> : <span className="kick-bubble-dot" />}
      </button>
    );
  }

  const posStyle = pos ? { left: pos.left, top: pos.top, right: 'auto', bottom: 'auto' } : {};

  return (
    <section ref={boxRef} className={`kick-widget ${live ? 'is-live' : ''}`} style={posStyle} aria-label={`${status.channel} on Kick`}>
      <header
        className="kick-bar"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <span className="kick-grip" title="Drag to move">
          <Icon name="drag_indicator" size={16} />
        </span>
        <span className="kick-bar-avatar">
          <Avatar src={status.avatar} size={26} />
          {live && <i className="kick-bar-pulse" />}
        </span>
        <span className="kick-who">
          <span className="kick-name">{status.channel}</span>
          <span className="kick-sub">
            {live ? (
              <>
                {status.category || 'Live on Kick'}
                {status.startedAt && (
                  <>
                    {' · '}
                    <Uptime since={status.startedAt} />
                  </>
                )}
              </>
            ) : status.followers != null ? (
              `${shortCount(status.followers)} followers`
            ) : (
              'Kick'
            )}
          </span>
        </span>
        {live ? (
          <span className="kick-badge is-live">
            <i /> Live
            {status.viewers != null && (
              <span className="kick-viewers">
                <Icon name="visibility" size={12} />
                {shortCount(status.viewers)}
              </span>
            )}
          </span>
        ) : (
          <span className="kick-badge">Offline</span>
        )}
        <span className="kick-ctrl">
          <a href={KICK_URL} target="_blank" rel="noopener noreferrer" title="Open on Kick">
            <Icon name="open_in_new" size={15} />
          </a>
          <button type="button" title={view === 'bar' ? 'Expand' : 'Minimize'} onClick={() => setView(view === 'bar' ? 'card' : 'bar')}>
            <Icon name={view === 'bar' ? 'expand_less' : 'expand_more'} size={18} />
          </button>
          <button type="button" className="kick-close" title="Close" onClick={close}>
            <Icon name="close" size={16} />
          </button>
        </span>
      </header>

      {view === 'card' &&
        (live ? (
          <>
            <div className="kick-player">
              <iframe
                title={`${status.channel} live on Kick`}
                src={`https://player.kick.com/${CHANNEL}?autoplay=true&muted=true`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
            {status.title && (
              <div className="kick-title" title={status.title}>
                {status.title}
              </div>
            )}
          </>
        ) : (
          <div className="kick-offline">
            <div className="kick-banner" style={status.banner ? { backgroundImage: `url("${status.banner}")` } : undefined} />
            <div className="kick-offline-body">
              <Avatar src={status.avatar} size={56} className="kick-offline-avatar" />
              <p className="kick-offline-title">Offline right now</p>
              <p className="kick-offline-text">Follow on Kick to get a ping when the next stream starts.</p>
              <a className="kick-follow shine-hover" href={KICK_URL} target="_blank" rel="noopener noreferrer">
                <Icon name="favorite" size={15} />
                Follow on Kick
              </a>
            </div>
          </div>
        ))}
    </section>
  );
}
